import { forwardRef } from 'react';

import { twMergeClsx } from 'utils/tw-with-clsx';
import { InputType, type InputRefType, type InputTypePropTypes } from 'views/shared/input-type';

interface InputPropTypes {
  error?: boolean | string | null;
  label?: string;
  containerClassName?: string;
  LeftIcon?: JSX.Element | null;
  RightIcon?: JSX.Element | null;
  isEmail?: boolean;
}

const inputStyles =
  'px-4 py-3 bg-sz-gray-100 outline-none h-10 md:h-[46px] rounded-[12px] text-base text-black font-semibold leading-6 border border-solid border-sz-gray-100';
const inputEmmailStyles =
  'pb-3 border-b border-sz-gray-700 text-base text-black font-semibold leading-6 outline-none w-full';

const Input = forwardRef<InputRefType, InputPropTypes & InputTypePropTypes>((props, ref) => {
  const {
    inputClassName,
    amount,
    error,
    containerClassName,
    label,
    name,
    id,
    value,
    isEmail,
    disabled,
    LeftIcon,
    RightIcon,
  } = props;

  const inputClassess = twMergeClsx(
    isEmail ? inputEmmailStyles : inputStyles,
    inputClassName,
    amount && '',
    error && '',
    disabled ? 'cursor-not-allowed' : 'hover:border-sz-yellow-200 focus:border-sz-yellow-200',
    LeftIcon && 'pl-[50px]',
    RightIcon && 'pr-[50px]',
  );

  return (
    <div className={twMergeClsx('text-sz-gray-300', containerClassName)}>
      {label ? (
        <label
          className={twMergeClsx(
            'text-sm leading-[14px] font-normal mb-2 block whitespace-nowrap',
            error && 'text-sz-red',
          )}
          htmlFor={name || id}
        >
          {label}
        </label>
      ) : null}
      <div className="relative">
        {LeftIcon && (
          <div className="absolute left-4 inset-y-0 flex items-center justify-center">
            {LeftIcon}
          </div>
        )}
        <InputType ref={ref} {...props} inputClassName={inputClassess} />
        <div className="absolute right-4 inset-y-0 flex items-center justify-center">
          {amount && <span className="text-base font-semibold text-black font-sans">₸</span>}
          {RightIcon}
        </div>
      </div>
      {typeof value === 'string' && error && typeof error === 'string' && (
        <div className="text-sz-red text-[11px] leading-3 text-left mt-[3px] lowercase whitespace-nowrap">
          {error}
        </div>
      )}
    </div>
  );
});

export { Input };
